<template>
  <div :class="['knowledge-chat-panel', `variant-${variant}`]">
    <header class="panel-header">
      <div class="header-title">
        <font-awesome-icon :icon="['fas', 'comments']" class="header-icon" />
        <span>Renox Assistant</span>
      </div>
      <div class="header-actions">
        <button
          v-if="expandable"
          type="button"
          class="header-action-button"
          @click="$emit('toggle-expand')"
          :aria-label="isExpanded ? 'Exit fullscreen' : 'Expand chat'"
          :title="isExpanded ? 'Exit fullscreen' : 'Expand chat'"
        >
          <font-awesome-icon :icon="['fas', isExpanded ? 'compress' : 'expand']" />
        </button>
        <button
          v-if="messages.length"
          type="button"
          class="header-action-button clear-button"
          @click="clearConversation"
          :disabled="isLoading"
          title="Clear conversation"
        >
          <font-awesome-icon :icon="['fas', 'trash']" />
          <span class="clear-label">Clear</span>
        </button>
      </div>
    </header>

    <div ref="messagesContainer" class="messages-container">
      <div v-if="!messages.length" class="empty-state">
        <div class="empty-icon">
          <font-awesome-icon :icon="['fas', 'robot']" />
        </div>
        <h3 class="empty-title">Hi, I'm your Renox Assistant</h3>
        <p class="empty-subtitle">
          I know and remember anything and everything about Renox - and I promise not to make things up when I don't know the answer
          <font-awesome-icon :icon="['fas', 'smile']" class="subtitle-smile" aria-hidden="true" />
        </p>
        <div class="suggestions">
          <button
            v-for="suggestion in suggestions"
            :key="suggestion"
            type="button"
            class="suggestion-chip"
            @click="sendSuggestion(suggestion)"
            :disabled="isLoading"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

      <transition-group v-else name="chat-message" tag="div" class="messages-list">
        <ChatMessageBubble
          v-for="message in messages"
          :key="message.id"
          :message="message"
          :user-first-name="userFirstName"
        />
        <div v-if="isLoading" key="typing-indicator" class="typing-row">
          <ChatTypingIndicator />
        </div>
      </transition-group>
      <div ref="scrollAnchor" class="scroll-anchor" />
    </div>

    <form class="input-area" @submit.prevent="handleSubmit">
      <textarea
        ref="inputField"
        v-model="draft"
        class="chat-input"
        rows="1"
        placeholder="Type your question..."
        :disabled="isLoading"
        @keydown="handleKeydown"
      />
      <button
        type="submit"
        class="send-button"
        :disabled="isLoading || !draft.trim()"
        aria-label="Send message"
      >
        <font-awesome-icon :icon="['fas', 'paper-plane']" />
      </button>
    </form>
  </div>
</template>

<script>
import { nextTick } from 'vue';
import { queryKnowledge } from '@/services/knowledgeChatService';
import { getUserInfo, canViewKnowledgeCitations } from '@/services/cookieService';
import ChatMessageBubble from './ChatMessageBubble.vue';
import ChatTypingIndicator from './ChatTypingIndicator.vue';

let messageIdCounter = 0;

function createMessageId() {
  messageIdCounter += 1;
  return `msg-${Date.now()}-${messageIdCounter}`;
}

function buildQueryHistory(messages, maxPairs = 3) {
  const pairs = [];
  for (let i = 0; i < messages.length - 1; i += 1) {
    const current = messages[i];
    const next = messages[i + 1];
    if (current.role === 'user' && next.role === 'assistant' && !next.error) {
      pairs.push({ question: current.text, answer: next.text });
      i += 1;
    }
  }
  return pairs.slice(-maxPairs);
}

export default {
  name: 'KnowledgeChatPanel',
  components: {
    ChatMessageBubble,
    ChatTypingIndicator,
  },
  props: {
    variant: {
      type: String,
      default: 'page',
      validator: (value) => ['page', 'widget'].includes(value),
    },
    expandable: {
      type: Boolean,
      default: false,
    },
    isExpanded: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle-expand'],
  data() {
    return {
      messages: [],
      draft: '',
      isLoading: false,
      userFirstName: 'You',
      canViewCitations: false,
      suggestions: [
        'What is the warranty period for RX-7000PLUS?',
        'What Renox products are available for residential customers?',
        'What are the installation requirements for the RX-7000PLUS?',
      ],
    };
  },
  created() {
    this.canViewCitations = canViewKnowledgeCitations();
    const userInfo = getUserInfo();
    if (userInfo?.firstName) {
      this.userFirstName = userInfo.firstName;
    }
  },
  methods: {
    handleKeydown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.handleSubmit();
      }
    },
    sendSuggestion(suggestion) {
      this.draft = suggestion;
      this.handleSubmit();
    },
    clearConversation() {
      this.messages = [];
      this.draft = '';
    },
    async scrollToBottom() {
      await nextTick();
      const anchor = this.$refs.scrollAnchor;
      if (anchor) {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    },
    async handleSubmit() {
      const question = this.draft.trim();
      if (!question || this.isLoading) {
        return;
      }

      this.messages.push({
        id: createMessageId(),
        role: 'user',
        text: question,
        timestamp: Date.now(),
      });
      this.draft = '';
      this.isLoading = true;
      await this.scrollToBottom();

      try {
        const history = buildQueryHistory(this.messages);
        const result = await queryKnowledge(question, history);
        this.messages.push({
          id: createMessageId(),
          role: 'assistant',
          text: result.answer || 'No answer was returned.',
          citations: this.canViewCitations ? result.citations : [],
          canAnswer: result.canAnswer,
          timestamp: Date.now(),
        });
      } catch (error) {
        const errorMessage = error?.message || 'Something went wrong. Please try again.';
        this.messages.push({
          id: createMessageId(),
          role: 'assistant',
          text: errorMessage,
          error: true,
          timestamp: Date.now(),
        });
      } finally {
        this.isLoading = false;
        await this.scrollToBottom();
        if (this.$refs.inputField) {
          this.$refs.inputField.focus();
        }
      }
    },
  },
};
</script>

<style scoped>
.knowledge-chat-panel {
  display: flex;
  flex-direction: column;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 1rem;
  box-shadow: 0 10px 25px -5px var(--shadow-color), 0 8px 10px -6px var(--shadow-color);
  overflow: hidden;
}

.variant-page {
  min-height: 70vh;
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
}

.variant-widget {
  height: 100%;
  border-radius: 1rem 1rem 0 0;
  border-bottom: none;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
}

.header-icon {
  color: #059669;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.header-action-button,
.clear-button {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.6rem;
  border: 1px solid var(--border-color);
  border-radius: 0.45rem;
  background: var(--card-bg);
  color: var(--text-secondary);
  font-size: 0.7rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.header-action-button:hover:not(:disabled),
.clear-button:hover:not(:disabled) {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.header-action-button:disabled,
.clear-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.empty-subtitle {
  margin: 0 0 1.25rem;
  font-size: 0.78rem;
  color: var(--text-tertiary);
  max-width: 22rem;
}

.subtitle-smile {
  margin-left: 0.25rem;
  color: #059669;
  font-size: 0.85em;
  vertical-align: middle;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: var(--bg-primary);
  min-height: 0;
}

.variant-page .messages-container {
  min-height: 50vh;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1rem;
  min-height: 100%;
}

.empty-icon {
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(5, 150, 105, 0.12);
  color: #059669;
  font-size: 1.25rem;
  margin-bottom: 1rem;
  animation: fadeInUp 0.4s ease both;
}

.empty-title {
  margin: 0 0 0.4rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  max-width: 28rem;
}

.suggestion-chip {
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background: var(--card-bg);
  color: var(--text-secondary);
  font-size: 0.72rem;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.suggestion-chip:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: #059669;
  box-shadow: 0 4px 12px var(--shadow-color);
}

.suggestion-chip:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.typing-row {
  display: flex;
  justify-content: flex-start;
}

.scroll-anchor {
  height: 1px;
}

.input-area {
  display: flex;
  align-items: flex-end;
  gap: 0.55rem;
  padding: 0.75rem;
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.chat-input {
  flex: 1;
  resize: none;
  min-height: 2.5rem;
  max-height: 7rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--input-border);
  border-radius: 0.65rem;
  background: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.82rem;
  line-height: 1.4;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.chat-input:focus {
  outline: none;
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.15);
}

.chat-input:disabled {
  opacity: 0.7;
}

.send-button {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: 50%;
  background: #059669;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.send-button:hover:not(:disabled) {
  background: #047857;
  transform: scale(1.05);
  box-shadow: 0 4px 14px rgba(5, 150, 105, 0.35);
}

.send-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .clear-label {
    display: none;
  }

  .variant-page {
    min-height: calc(100vh - 8rem);
    border-radius: 0.75rem;
  }
}
</style>
